import { api, BASE, request } from '../services/api/apiClient';

// Sending invoice / quotation PDFs to customers.
// kind: 'invoices' | 'quotations' (the API collection).
export type ShareKind = 'invoices' | 'quotations';

/** Public link to the PDF (created on first use, same link afterwards). Anyone with it can open that one PDF. */
export async function shareLink(kind: ShareKind, id: string): Promise<string> {
  const { data } = await api.post<{ data: { path: string } }>(`/${kind}/${id}/share`);
  return new URL(`${BASE}${data.path}`, window.location.origin).href;
}

export const stopSharing = (kind: ShareKind, id: string) => api.del(`/${kind}/${id}/share`);

/** Old-style copy through a hidden text box. Works where the Clipboard API is missing (plain http on a LAN address). */
export function legacyCopy(text: string): boolean {
  const box = document.createElement('textarea');
  box.value = text;
  box.setAttribute('readonly', '');
  box.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
  document.body.appendChild(box);
  box.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  box.remove();
  return ok;
}

/**
 * Copies text that is still being fetched (e.g. a share link being created).
 * Call it straight from the click: the clipboard write is started before any waiting,
 * so browsers that only allow copying during a click (Safari, Firefox) accept it.
 * Resolves to { text, copied }; copied is false when every method was blocked.
 */
export async function copyPending(textPromise: Promise<string>): Promise<{ text: string; copied: boolean }> {
  const clip = window.isSecureContext ? navigator.clipboard : undefined;
  if (clip?.write && window.ClipboardItem) {
    try {
      await clip.write([new window.ClipboardItem({ 'text/plain': textPromise.then((t) => new Blob([t], { type: 'text/plain' })) })]);
      return { text: await textPromise, copied: true };
    } catch {
      /* try the simpler ways below */
    }
  }
  const text = await textPromise;
  if (clip?.writeText) {
    try {
      await clip.writeText(text);
      return { text, copied: true };
    } catch {
      /* fall through */
    }
  }
  return { text, copied: legacyCopy(text) };
}

/** The PDF as a File, ready to attach. */
export async function pdfFile(kind: ShareKind, id: string, fileName: string): Promise<File> {
  const res = await request(`/${kind}/${id}/pdf`, { raw: true });
  const blob = await res.blob();
  return new File([blob], `${fileName.replace(/[^\w.-]+/g, '_')}.pdf`, { type: 'application/pdf' });
}

/** True when this browser can hand a PDF file to other apps (phones, Chrome/Edge on Windows). */
export const canShareFiles = () => {
  try {
    return !!navigator.canShare && navigator.canShare({ files: [new File(['x'], 'x.pdf', { type: 'application/pdf' })] });
  } catch {
    return false;
  }
};

export function downloadFile(file: File) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
}

/** Indian mobile -> wa.me number (91XXXXXXXXXX), or '' to let WhatsApp ask for the contact. */
export const waNumber = (phone?: string | null) => {
  const digits = (phone ?? '').replace(/\D/g, '').slice(-10);
  return digits.length === 10 ? `91${digits}` : '';
};

/**
 * Opens WhatsApp with a message that includes the PDF link.
 * The tab is opened straight away (before waiting for the link) so the browser does not block it as a pop-up.
 */
export async function sendOnWhatsApp(kind: ShareKind, id: string, phone: string | null | undefined, message: string) {
  const tab = window.open('', '_blank');
  try {
    const link = await shareLink(kind, id);
    const url = `https://wa.me/${waNumber(phone)}?text=${encodeURIComponent(`${message}\n\n${link}`)}`;
    if (tab) tab.location.href = url;
    else window.location.href = url;
    return link;
  } catch (err) {
    tab?.close();
    throw err;
  }
}
